import React, { useEffect, useRef } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { fg } from '@atlassian/jira-feature-gating';
import { useViewEpicPanelExperienceTracker } from './services/experience-tracker/index.tsx';
import { useEpicDetailsQueryLazy } from './services/epic/get/index.tsx';
import type { Props } from './ui/epic/main.tsx';
import type EpicPanelType from './ui/epic/main.tsx';
import { LoadingSkeleton } from './ui/epic/epic-panel/loading-skeleton/index.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const EpicPanel = lazyAfterPaint<typeof EpicPanelType>(
	() => import(/* webpackChunkName: "async-software-backlog-panel" */ './ui/epic/main.tsx'),
	{ ssr: false },
);

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (props: Props) => {
	const { fireErrorBoundaryExperienceFail } = useViewEpicPanelExperienceTracker({
		isClassic: props.isCompanyManaged || false,
	});

	return (
		<JSErrorBoundary
			id="async.jira.backlog.panel"
			packageName="jiraSoftwareBacklogPanel"
			fallback="flag"
			teamName="a4t-pandora"
			onError={fireErrorBoundaryExperienceFail}
		>
			<Placeholder
				name="backlog-panel-boundary"
				fallback={
					fg('prefetch-epics-data-backlog') ? (
						<LoadingSkeleton canCreate={props.canCreate} hierarchyName={props.hierarchyName} />
					) : (
						<></>
					)
				}
			>
				<EpicPanel {...props} />
			</Placeholder>
		</JSErrorBoundary>
	);
};

export const usePreloadEpicPanel = (
	isEpicPanelOpen: boolean,
	boardId: number,
	isCompanyManaged: boolean,
) => {
	const isPreloaded = useRef<boolean>(false);
	const [getEpicDetails] = useEpicDetailsQueryLazy({ boardId, isCompanyManaged });

	useEffect(() => {
		if (isEpicPanelOpen && isPreloaded && !isPreloaded.current) {
			EpicPanel.preload();
			isPreloaded.current = true;
			if (!fg('prefetch-epics-data-backlog')) {
				getEpicDetails();
			}
		}
	}, [getEpicDetails, isEpicPanelOpen]);

	return null;
};
